// Dropdown menu primitive: a trigger button plus an anchored popover of items.
// Closes on outside-click, Escape, or item activation. Generalizes the share
// popover pattern for the export/download and item context menus.

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Icon, type IconName } from "./Icon";

export interface MenuItem {
  label: string;
  icon?: IconName;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Highlighted (butter) item — used for Trust, which must not read as routine. */
  emphasis?: boolean;
}

interface Props {
  trigger: ReactNode; // contents of the trigger button
  items: MenuItem[];
  align?: "left" | "right";
  ariaLabel: string;
  triggerClassName?: string;
  /**
   * Render a chevron after the trigger so the button visibly reads as a
   * dropdown. Off by default for icon-only triggers (⋯ already implies more).
   */
  caret?: boolean;
}

export function Menu({
  trigger,
  items,
  align = "right",
  ariaLabel,
  triggerClassName,
  caret = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // pointerdown as well as mousedown: an open popover now paints ABOVE the
    // cards after it (see .card:has(.menu-popover) in theme.css), so it swallows
    // taps instead of hiding behind them — and iOS Safari does not deliver the
    // mouse-compat sequence to `document` when the tap lands on a plain element,
    // which would leave the menu undismissable on a phone. Pointer events there
    // come from touch directly, not the compat path.
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="menu-root" ref={rootRef}>
      <button
        type="button"
        className={triggerClassName ?? "btn btn-ghost"}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        {trigger}
        {caret && (
          <Icon
            name="chevron-down"
            size={16}
            className={`menu-caret${open ? " is-open" : ""}`}
          />
        )}
      </button>
      {open && (
        <div
          id={id}
          className={`menu-popover menu-popover--${align}`}
          role="menu"
          aria-label={ariaLabel}
        >
          {items.map((item, i) => (
            <button
              key={`${item.label}-${i}`}
              type="button"
              role="menuitem"
              className={`menu-item${item.danger ? " menu-item--danger" : ""}${
                item.emphasis ? " menu-item--emphasis" : ""
              }`}
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.icon && <Icon name={item.icon} size={18} />}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
